import clsx from 'clsx';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type TableHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/* ---------------------------------------------------------------- Table */

/**
 * Data table: horizontal scroll inside the wrapper (never the page), optional sticky header and
 * density. Add `cards` to turn rows into labelled cards ≤ 640px (cells need `data-label`,
 * the actions cell `className="ui-table__actions"`).
 */
export function Table({
  density = 'normal',
  sticky = true,
  cards,
  stickyFirst,
  className,
  wrapClassName,
  maxHeight,
  ...rest
}: TableHTMLAttributes<HTMLTableElement> & {
  density?: 'compact' | 'normal' | 'comfortable';
  sticky?: boolean;
  cards?: boolean;
  /** pin the first (name) column while the table scrolls sideways; mark a pinned actions
   *  column with className="ui-table__sticky-end" on its th/td */
  stickyFirst?: boolean;
  wrapClassName?: string;
  /** scroll inside the wrapper vertically too (sticky header then sticks to the wrapper) */
  maxHeight?: number | string;
}) {
  return (
    <div
      className={clsx('ui-table-wrap', wrapClassName)}
      style={maxHeight ? { maxHeight } : undefined}
    >
      <table
        className={clsx(
          'ui-table',
          density !== 'normal' && `ui-table--${density}`,
          sticky && 'ui-table--sticky',
          cards && 'ui-table--cards',
          stickyFirst && 'ui-table--sticky-first',
          className,
        )}
        {...rest}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- Toast */

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';
interface ToastItem {
  id: number;
  tone: ToastTone;
  message: ReactNode;
  action?: { label: string; onClick: () => void };
}
interface ToastApi {
  /** shows a toast; returns its id. Default duration 5 s (danger 8 s); 0 = until dismissed */
  toast: (t: {
    message: ReactNode;
    tone?: ToastTone;
    duration?: number;
    action?: ToastItem['action'];
  }) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
const TOAST_ICON: Record<ToastTone, ReactNode> = {
  info: <Info size={18} />,
  success: <CheckCircle2 size={18} />,
  warning: <AlertTriangle size={18} />,
  danger: <XCircle size={18} />,
};

/** Mounted once at the root route; features call `useToast().toast({...})`. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const dismiss = useCallback((id: number) => {
    setItems((xs) => xs.filter((x) => x.id !== id));
    const tm = timers.current.get(id);
    if (tm) clearTimeout(tm);
    timers.current.delete(id);
  }, []);
  const toast = useCallback<ToastApi['toast']>(
    ({ message, tone = 'info', duration, action }) => {
      const id = ++seq.current;
      setItems((xs) => [...xs.slice(-3), { id, tone, message, action }]);
      const ms = duration ?? (tone === 'danger' ? 8000 : 5000);
      if (ms > 0)
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), ms),
        );
      return id;
    },
    [dismiss],
  );
  useEffect(() => {
    const map = timers.current;
    return () => map.forEach((tm) => clearTimeout(tm));
  }, []);
  const api = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="ui-toasts" aria-live="polite" aria-relevant="additions">
        {items.map((it) => (
          <div
            key={it.id}
            className={clsx('ui-toast', `ui-toast--${it.tone}`)}
            role={it.tone === 'danger' ? 'alert' : 'status'}
          >
            <span className="ui-toast__icon" aria-hidden="true">
              {TOAST_ICON[it.tone]}
            </span>
            <div className="ui-toast__msg">{it.message}</div>
            {it.action ? (
              <button
                type="button"
                className="ui-toast__action"
                onClick={() => {
                  it.action!.onClick();
                  dismiss(it.id);
                }}
              >
                {it.action.label}
              </button>
            ) : null}
            <button
              type="button"
              className="ui-toast__close"
              aria-label={t('ui.dismiss')}
              onClick={() => dismiss(it.id)}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const NOOP_TOASTS: ToastApi = { toast: () => 0, dismiss: () => undefined };
/** Toast API (a no-op outside ToastProvider, e.g. isolated component tests). */
export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NOOP_TOASTS;
}

/* --------------------------------------------------------------- Dialog */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal dialog (portal, focus trap, Esc / backdrop closes, focus returns to the opener).
 * `footer` is the action row (primary action last). On phones it becomes a bottom sheet.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** false = no close button, Esc and backdrop do nothing (blocking confirmations) */
  dismissible?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('common');
  const titleId = useId();
  const descId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const el = panel.current;
    const first =
      el?.querySelector<HTMLElement>('[data-autofocus]') ??
      el?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? el)?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation();
        closeRef.current();
      }
      if (e.key !== 'Tab' || !el) return;
      const nodes = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (nodes.length === 0) return;
      const a = nodes[0]!;
      const z = nodes[nodes.length - 1]!;
      if (e.shiftKey && document.activeElement === a) {
        e.preventDefault();
        z.focus();
      } else if (!e.shiftKey && document.activeElement === z) {
        e.preventDefault();
        a.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [open, dismissible]);

  if (!open) return null;
  return createPortal(
    <div
      className="ui-dialog__backdrop"
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={clsx('ui-dialog', `ui-dialog--${size}`, className)}
      >
        <div className="ui-dialog__head">
          <h2 id={titleId} className="ui-dialog__title">
            {title}
          </h2>
          {dismissible ? (
            <button
              type="button"
              className="ui-btn ui-btn--ghost ui-btn--icon ui-btn--sm"
              aria-label={t('close')}
              onClick={onClose}
            >
              <X size={18} aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {description ? (
          <p id={descId} className="ui-dialog__desc">
            {description}
          </p>
        ) : null}
        {children ? <div className="ui-dialog__body">{children}</div> : null}
        {footer ? <div className="ui-dialog__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
